import {useAnnounceOnce} from '@github-ui/board/frames'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {hashKey, keepPreviousData, useQuery} from '@github-ui/react-query'
import {usePreviewQueryContext} from '@github-ui/saved-views/contexts/PreviewQueryContext'
import {Banner} from '@primer/react'
import {type ReactNode, useMemo} from 'react'

import {useSavedViewConfiguration} from '../../../contexts/views/SavedViewConfigurationProvider'
import {createNewViewBoardAdapter, fetchNewViewBoard} from '../../../helpers/create-saved-view-board-adapter'
import {useLayoutPlaceholder} from '../../../hooks/use-saved-view-navigation'
import {SavedViewLayoutSwitch} from '../../SavedViewLayoutSwitch'
import {useSavedViewFields} from '../../saved-view-options/use-saved-view-fields'
import {IssuesSavedViewBoard, type SavedViewBoardLayout} from './IssuesSavedViewBoard'

const NEW_VIEW_BOARD_FAILED = 'Could not load the board preview'
const NEW_VIEW_BOARD_FAILED_DETAIL = 'The view can still be saved.'

function NewViewBoardPreviewError() {
  // A banner is not a live region, and the failure replaces the board without moving focus.
  useAnnounceOnce(`${NEW_VIEW_BOARD_FAILED}. ${NEW_VIEW_BOARD_FAILED_DETAIL}`, true)

  return (
    <Banner
      variant="critical"
      title={NEW_VIEW_BOARD_FAILED}
      description={NEW_VIEW_BOARD_FAILED_DETAIL}
      className="tmp-mt-3"
    />
  )
}

/** A failure shows in place, so the form around the preview keeps working. */
export function NewViewBoardPreview({
  basePath,
  routeId,
  list,
  layout,
}: {
  basePath: string
  routeId: string
  /** The preview the board replaces, kept on screen while the first board loads. */
  list: ReactNode
  layout: SavedViewBoardLayout
}) {
  const fieldDefinitions = useSavedViewFields(basePath).data?.fields
  const {groupBy, swimlaneBy} = useSavedViewConfiguration()
  const previewQuery = usePreviewQueryContext()
  if (!previewQuery) throw new Error('NewViewBoardPreview needs a PreviewQueryProvider')
  const {currentQuery: query, onQueryChange} = previewQuery
  const {adapter, isError, isPlaceholderData} = useNewViewBoardAdapter({basePath, routeId, query, groupBy, swimlaneBy})
  const pendingLayout = adapter === undefined && !isError ? 'board' : null
  const layoutPlaceholder = useLayoutPlaceholder(pendingLayout)

  if (isError) return <NewViewBoardPreviewError />

  return (
    <SavedViewLayoutSwitch
      boardContainerStyle={layout.containerStyle}
      layout={adapter === undefined ? 'list' : 'board'}
      navigation={{isNavigating: isPlaceholderData, pendingLayout, layoutPlaceholder}}
      renderList={() => list}
      renderBoard={({isNavigating}) =>
        adapter && (
          <div className={layout.className}>
            {/* Keyed by the board, so a board that failed to draw gives way to the next one. */}
            <ErrorBoundary
              key={hashKey(adapter.key)}
              boundaryName="NewViewBoardPreview"
              fallback={<NewViewBoardPreviewError />}
            >
              <IssuesSavedViewBoard
                adapter={adapter}
                query={query}
                isNavigating={isNavigating}
                fieldDefinitions={fieldDefinitions}
                onQueryChange={onQueryChange}
                viewportAnchor="viewport"
                boardClassName={layout.boardClassName}
                readOnly
              />
            </ErrorBoundary>
          </div>
        )
      }
    />
  )
}

function useNewViewBoardAdapter({
  basePath,
  routeId,
  query,
  groupBy,
  swimlaneBy,
}: {
  basePath: string
  routeId: string
  query: string
  groupBy: string | null
  swimlaneBy: string | null
}) {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- A preview of a view not saved yet has nothing worth keeping across visits, and the board endpoint returns plain JSON validated by `fetchNewViewBoard`, not a Protobuf payload.
  const {data, isError, isPlaceholderData} = useQuery({
    queryKey: ['new-view-board', basePath, routeId, query, groupBy, swimlaneBy],
    // Timed here rather than read from `dataUpdatedAt`, which is the loading query's 0 while a
    // previous board stands in, and would key that board anew and redraw it from its first page.
    queryFn: async ({signal}) => ({
      board: await fetchNewViewBoard({basePath, routeId, query, groupBy, swimlaneBy, signal}),
      fetchedAt: Date.now(),
    }),
    placeholderData: keepPreviousData,
    // A board drawn before is reused as it was, rather than redrawn under a reader paging it.
    staleTime: Infinity,
    retry: false,
  })
  const adapter = useMemo(
    () => data && createNewViewBoardAdapter({board: data.board, basePath, routeId, dataUpdatedAt: data.fetchedAt}),
    [data, basePath, routeId],
  )

  return {adapter, isError, isPlaceholderData}
}
